import React from 'react';
import styled from 'styled-components';
import { LockOutlined } from '@ant-design/icons';
import { Form, Input, Button, Space } from 'antd';
import { useIntl, FormattedMessage } from 'react-intl';
import PasswordStrengthBar from 'Common/Components/PasswordStrengthBar';

const { Item } = Form;
const { Password } = Input;

const StyledLockOutlined = styled(LockOutlined)`
  font-size: 18px;
  color: ${(props) => props.theme.iconColor};
`;

const PasswordInput = styled(Password)`
  height: 32px;
  border-radius: 6px;
  .ant-input {
    font-size: 15px;
    border: none;
  }
  .ant-input:not(:first-child) {
    padding-left: 12px !important;
  }
`;

interface Props {
  handleSubmit: (data: FormValues) => void;
}

export interface FormValues {
  password: string;
  passwordConfirmation: string;
}

const ResetPasswordForm: React.FC<Props> = ({ handleSubmit }) => {
  const intl = useIntl();
  const [form] = Form.useForm<FormValues>();
  return (
    <Form
      form={form}
      layout="vertical"
      css="width: 100%;"
      onFinish={(values) => {
        handleSubmit(values);
        form.resetFields();
      }}
    >
      <Space
        orientation="vertical"
        css="width: 100%; row-gap: 0px;"
        size="middle"
      >
        <div>
          <Item
            name="password"
            validateFirst
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  defaultMessage: 'This field is required',
                }),
              },
              {
                min: 8,
                message: intl.formatMessage(
                  {
                    defaultMessage:
                      'The password must contain at least {len} characters',
                  },
                  { len: 8 }
                ),
              },
              {
                pattern: /[a-z]/,
                message: intl.formatMessage({
                  defaultMessage:
                    'The password must contain at least one lowercase character',
                }),
              },
              {
                pattern: /[A-Z]/,
                message: intl.formatMessage({
                  defaultMessage:
                    'The password must contain at least one uppercase character',
                }),
              },
            ]}
          >
            <PasswordInput
              size="large"
              prefix={<StyledLockOutlined />}
              placeholder={intl.formatMessage({
                defaultMessage: 'New password',
              })}
            />
          </Item>
          <Item
            noStyle
            shouldUpdate={(prev, next) => prev.password !== next.password}
          >
            {() =>
              form.getFieldValue('password') && (
                <div css="margin-top: 4px; margin-bottom: 8px;">
                  <PasswordStrengthBar
                    password={form.getFieldValue('password')}
                  />
                  <span css="font-size: 11px; color: rgba(0, 0, 0, .45)">
                    <FormattedMessage defaultMessage="Password strength" />
                  </span>
                </div>
              )
            }
          </Item>
        </div>
        <Item
          name="passwordConfirmation"
          dependencies={['password']}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                defaultMessage: 'This field is required',
              }),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  intl.formatMessage({
                    defaultMessage: 'The two passwords do not match',
                  })
                );
              },
            }),
          ]}
        >
          <PasswordInput
            size="large"
            prefix={<StyledLockOutlined />}
            placeholder={intl.formatMessage({
              defaultMessage: 'Confirm new password',
            })}
          />
        </Item>
        <Item css="margin: 0;">
          <Button type="primary" size="large" htmlType="submit" block>
            <FormattedMessage defaultMessage="Reset password" />
          </Button>
        </Item>
      </Space>
    </Form>
  );
};

export default ResetPasswordForm;
